/** The Library's filter pane and results pane (toolbar, grid or table, paging). */
import { createContext, useContext, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { Archive, BadgeCheck, Bookmark, Check, Clapperboard, EyeOff, FilterX, FolderOpen, LayoutGrid, List, Plus, RotateCcw, Search, SearchX, Sparkles, Trash2, X } from 'lucide-react';
import {
  ADDED_FILTERS, ADDED_LABELS, AVAILABILITY_FILTERS, BULK_ACTION_LIMIT, AVAILABILITY_FILTER_LABELS, METADATA_FILTERS, METADATA_FILTER_LABELS, QC_FILTERS, QC_FILTER_LABELS, RELEASE_181_ERRORS, searchTargets,
  type AssetCategoryDTO, type AssetSummary, type MetadataSchemaDefinition, type StorageLocationSummary, type StratumDTO,
} from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { LIBRARY_PAGE_SIZE, libraryCapped, useFacetValues, useLibraryFacets, useMe, type LibraryFilters, type useLibrary } from '../../api/hooks';
import { SendToEditDialog } from '../../components/SendToEdit';
import { useDebounced } from '../../lib/useDebounced';
import { useDismissOnOutside } from '../../lib/useDismissOnOutside';
import { PaneControls } from '../../components/Panes';
import { STATUS_COLORS, STATUS_LABELS } from '../../components/Status';
import { pickFiles } from '../../components/DropZone';
import { toast } from '../../components/Toasts';
import { Segmented } from '../../components/Segmented';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { useBulkRestore, useCanRestore } from '../../components/ArchiveRestore';
import { RunQcDialog } from '../../components/QualityControl';
import { ColumnsMenu, type useLibraryColumns } from '../LibraryColumns';
import { EMPTY_ADVANCED, advancedSummary, usableAdvanced } from '../AdvancedSearch';
import { AssetCard, AssetTable, useEntryOrder } from './LibraryItems';
import { SavedSearches } from './SavedSearches';
import { LibrarySelectionBar } from './LibrarySelectionBar';
import type { LibrarySelection, SelectGesture } from './useLibrarySelection';
import { MasterFacet, MasterFilterChips } from './MasterFacet';
import { RightsFacet, RightsFilterChip } from './RightsFacet';
import { facetLabel, mediaKindLabel, type FacetKey, type LibraryFilterState } from './useLibraryFilters';
import { aboutCount, approxCount } from '../../lib/format';
// 1.4.0 asset groups: G1's filter chip and sort, G3's Grouped view.
import { GROUP_SORT_OPTION, GroupFilterChip } from './group-filter/GroupFilter';
import { GroupedResults, GroupedToggle } from './grouped/GroupedView';

/** Whether the facet counts in the filter pane come from a sample (1.0.0-rc.13). */
const ApproximateFacets = createContext(false);

export function FiltersPane({ f, listVersion, schema, categories, locations, canDelete }: {
  f: LibraryFilterState; schema: MetadataSchemaDefinition | undefined;
  /** The list's version (useLibrary): the facet counts are asked after its page, and again when it is fetched again. */
  listVersion: number | undefined;
  categories: AssetCategoryDTO[]; locations: StorageLocationSummary[]; canDelete: boolean;
}) {
  const { filters, setFilters, toggle } = f;
  const facetFields = (schema?.fields ?? []).filter((x) => x.facet);
  // The context keyword suggestions are counted in: the list's own filters and search.
  const context: LibraryFilters = { ...f.activeFilters, q: f.q, sort: f.sort, advanced: f.advancedQuery };
  // The facet counts in their own request (1.0.0-rc.13 third round), only while this pane is shown. Approximate
  // counts come from a sample (over 10,000 matches): shown as "≈ 12,300".
  const facetsQ = useLibraryFacets(context, { after: listVersion });
  const facets = facetsQ.data?.facets;
  const facetsApproximate = facetsQ.data?.approximate ?? false;
  return (
    <aside className="pane raised" aria-label="Filters">
      <div className="pane-header"><span className="pane-title">Filter</span><div style={{ flex: 1 }} /><PaneControls /></div>
      <div className="pane-body">
        {/* 1.4.0 (G1): the group filter (?group=). */}
        <GroupFilterChip f={f} />
        {f.filtersActive && (
          <div className="facets-clear">
            <button className="btn sm" onClick={f.clearFilters} data-testid="clear-filters" title="Remove every filter in this pane (the search stays)">
              <FilterX size={13} /> Clear filters
            </button>
          </div>
        )}
        <ApproximateFacets.Provider value={facetsApproximate}>
        <div className="facets" data-approximate={facetsApproximate || undefined}>
          <SavedSearches onOpen={f.applySaved} />
          <div className="facet" data-testid="added-filter">
            <h3>Added</h3>
            {ADDED_FILTERS.map((k) => (
              <button key={k} className="facet-option" aria-pressed={filters.added === k} onClick={() => setFilters((x) => ({ ...x, added: x.added === k ? null : k }))}>
                <span className="check">{filters.added === k && <Check size={11} strokeWidth={3} />}</span>
                {ADDED_LABELS[k]}
              </button>
            ))}
          </div>
          <div className="facet" data-testid="metadata-filter">
            <h3>Metadata</h3>
            {METADATA_FILTERS.map((k) => (
              <button key={k} className="facet-option" aria-pressed={filters.metadata === k} onClick={() => setFilters((x) => ({ ...x, metadata: x.metadata === k ? null : k }))}>
                <span className="check">{filters.metadata === k && <Check size={11} strokeWidth={3} />}</span>
                {METADATA_FILTER_LABELS[k]}
              </button>
            ))}
          </div>
          <div className="facet" data-testid="availability-filter">
            <h3>Original</h3>
            {AVAILABILITY_FILTERS.map((k) => (
              <button key={k} className="facet-option" aria-pressed={filters.availability === k} onClick={() => setFilters((x) => ({ ...x, availability: x.availability === k ? null : k }))}>
                <span className="check">{filters.availability === k && <Check size={11} strokeWidth={3} />}</span>
                {AVAILABILITY_FILTER_LABELS[k]}
              </button>
            ))}
          </div>
          <RightsFacet f={f} />
          <div className="facet" data-testid="qc-filter">
            <h3>QC</h3>
            {QC_FILTERS.map((k) => (
              <button key={k} className="facet-option" aria-pressed={filters.qc === k} onClick={() => setFilters((x) => ({ ...x, qc: x.qc === k ? null : k }))} data-testid={`qc-filter-${k}`}>
                <span className="check">{filters.qc === k && <Check size={11} strokeWidth={3} />}</span>
                {QC_FILTER_LABELS[k]}
              </button>
            ))}
          </div>
          <FacetGroup title="Status" values={facets?.status} selected={filters.status} onToggle={(v) => toggle('status', v)}
            label={(v) => STATUS_LABELS[v as keyof typeof STATUS_LABELS] ?? v} swatch={(v) => STATUS_COLORS[v]} order={['ready', 'processing', 'registered', 'partial', 'failed', 'placeholder']}
            help="Ready: processing has finished. Online and Archived assets are both ready." />
          <FacetGroup title="Class" values={facets?.category} selected={filters.category} onToggle={(v) => toggle('category', v)}
            label={(v) => (v === 'none' ? 'No class' : categories.find((c) => c.id === v)?.name ?? 'Removed class')}
            swatch={(v) => categories.find((c) => c.id === v)?.color} order={[...categories.map((c) => c.id), 'none']} />
          <FacetGroup title="Media" values={facets?.kind} selected={filters.kind} onToggle={(v) => toggle('kind', v)} label={mediaKindLabel} />
          <MasterFacet f={f} />
          {facetFields.map((x) => (x.type === 'tags'
            ? <KeywordFacet key={x.id} fieldId={x.id} title={x.label} selected={f.selectedFor(`field:${x.id}`)} onToggle={(v) => toggle(`field:${x.id}`, v)} context={context} />
            : <FacetGroup key={x.id} title={x.label} values={facets?.[x.id]} selected={f.selectedFor(`field:${x.id}`)}
                onToggle={(v) => toggle(`field:${x.id}`, v)} label={(v) => facetLabel(schema, x.id, v)} searchable={(x.options?.length ?? 0) > 20} testId={`facet-${x.id}`} />
          ))}
          <FacetGroup title="Stored in" values={facets?.location} selected={filters.location} onToggle={(v) => toggle('location', v)}
            label={(v) => locations.find((l) => l.id === v)?.name ?? 'Storage'} />
          <div className="facet">
            <button className="facet-option" aria-pressed={filters.includeDismissed} onClick={() => setFilters((x) => ({ ...x, includeDismissed: !x.includeDismissed }))}>
              <span className="check">{filters.includeDismissed && <Check size={11} strokeWidth={3} />}</span>
              <EyeOff size={13} /> Show dismissed
            </button>
            {/* 1.6.0: the moments a search finds, without AI suggestions nobody accepted yet. */}
            <button className="facet-option" aria-pressed={Boolean(filters.acceptedOnly)} onClick={() => setFilters((x) => ({ ...x, acceptedOnly: !x.acceptedOnly }))}
              data-testid="moments-accepted-only" title="Moments found by a search leave out AI suggestions that nobody has accepted yet">
              <span className="check">{filters.acceptedOnly && <Check size={11} strokeWidth={3} />}</span>
              <Sparkles size={13} /> Moments: accepted only
            </button>
            {canDelete && (
              <Link className="facet-option" to="/recycle-bin" data-testid="recycle-bin-link">
                <span className="check" style={{ visibility: 'hidden' }} />
                <Trash2 size={13} /> Recycle bin
              </Link>
            )}
          </div>
        </div>
        </ApproximateFacets.Provider>
      </div>
    </aside>
  );
}

/**
 * A keyword (tags) facet: the chosen values as chips, and a search box that suggests values as you type
 * (from the server, counted in the current filters). While the box is empty the five most used are offered.
 */
function KeywordFacet({ fieldId, title, selected, onToggle, context }: {
  fieldId: string; title: string; selected: string[]; onToggle: (v: string) => void; context: LibraryFilters;
}) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const anchor = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  useDismissOnOutside(anchor, open, () => setOpen(false));
  const typed = useDebounced(text.trim(), 150);
  const top = useFacetValues(fieldId, '', context, { limit: 5 });
  const found = useFacetValues(fieldId, typed, context, { enabled: open && typed.length > 0 });
  const suggestions = typed ? found.data?.items ?? [] : [];
  const showList = open && text.trim().length > 0;
  const pick = (v: string) => {
    onToggle(v);
    setText('');
    setActive(0);
    input.current?.focus();
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, Math.max(suggestions.length - 1, 0))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const s = suggestions[active];
      if (s) pick(s.value);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      if (text) setText(''); else { setOpen(false); input.current?.blur(); }
    } else if (e.key === 'Backspace' && !text && selected.length) onToggle(selected.at(-1)!);
  };
  return (
    <div className="facet keyword-facet" data-testid={`facet-${fieldId}`} ref={anchor}>
      <h3>{title}</h3>
      {selected.length > 0 && (
        <div className="keyword-chips">
          {selected.map((v) => (
            <span className="chip" key={v} data-testid="keyword-chip" data-value={v}>
              <span className="chip-label">{v}</span>
              <button aria-label={`Remove keyword ${v}`} onClick={() => onToggle(v)}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="keyword-search">
        <Search size={13} aria-hidden />
        <input ref={input} className="input" type="search" value={text} placeholder={`Find ${title.toLowerCase()}…`}
          role="combobox" aria-expanded={showList} aria-controls={showList ? listId : undefined} aria-autocomplete="list" aria-label={`Find ${title.toLowerCase()}`}
          aria-activedescendant={showList && suggestions[active] ? `${listId}-${active}` : undefined}
          onChange={(e) => { setText(e.target.value); setOpen(true); setActive(0); }} onFocus={() => setOpen(true)} onKeyDown={onKeyDown} data-testid={`facet-search-${fieldId}`} />
        {showList && (
          <ul id={listId} className="keyword-suggestions" role="listbox" aria-label={`${title} suggestions`} data-testid="keyword-suggestions">
            {suggestions.map((s, i) => (
              <li key={s.value} id={`${listId}-${i}`} role="option" aria-selected={i === active} className={i === active ? 'active' : ''}
                onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => pick(s.value)} data-testid="keyword-suggestion" data-value={s.value}>
                <span className="check">{selected.includes(s.value) && <Check size={11} strokeWidth={3} />}</span>
                <span className="name">{s.value}</span><span className="count">{suggestionCount(s, found.data?.approximate)}</span>
              </li>
            ))}
            {!suggestions.length && <li className="muted empty">{found.isFetching || typed !== text.trim() ? 'Searching…' : `No ${title.toLowerCase()} match “${text.trim()}”`}</li>}
          </ul>
        )}
      </div>
      {!text.trim() && (top.data?.items ?? []).filter((t) => !selected.includes(t.value)).map((t) => (
        <button key={t.value} className="facet-option" aria-pressed={false} onClick={() => onToggle(t.value)} data-testid="keyword-top">
          <span className="check" />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.value}</span>
          <span className="count">{suggestionCount(t, top.data?.approximate)}</span>
        </button>
      ))}
    </div>
  );
}

/** A suggestion's count: "1,000+" when it stopped at the cap (1.0.0-rc.13), "≈ 12,300" when scaled, else exact. */
function suggestionCount(s: { count: number; atLeast?: boolean }, approximate?: boolean): string | number {
  if (s.atLeast) return `${s.count.toLocaleString('en')}+`;
  return approximate ? approxCount(s.count) : s.count;
}

function FacetGroup({ title, values, selected, onToggle, label, swatch, order, limit = 20, searchable = false, testId, help }: {
  title: string; values: Record<string, number> | undefined; selected: string[]; onToggle: (v: string) => void;
  label: (v: string) => string; swatch?: (v: string) => string | undefined; order?: string[]; limit?: number;
  /** A search box over the options (vocabularies with more than 20). */
  searchable?: boolean; testId?: string;
  /** A line under the title (1.9.1: the Status filter says Online and Archived are both ready). */
  help?: string;
}) {
  const [find, setFind] = useState('');
  // Counts from a sample above 10,000 matches (1.0.0-rc.13) show as "≈ 12,300".
  const approximate = useContext(ApproximateFacets);
  const entries = Object.entries(values ?? {});
  // A chosen value no asset has any more stays in the list (with 0) so it can be cleared.
  for (const s of selected) if (!entries.some(([k]) => k === s)) entries.push([s, 0]);
  if (!entries.length) return null;
  entries.sort((a, b) => (order ? order.indexOf(a[0]) - order.indexOf(b[0]) : b[1] - a[1] || a[0].localeCompare(b[0])));
  const needle = find.trim().toLowerCase();
  const shown = needle ? entries.filter(([v]) => label(v).toLowerCase().includes(needle) || selected.includes(v)) : entries;
  return (
    <div className="facet" data-testid={testId}>
      <h3>{title}</h3>
      {help && <p className="facet-help faint" data-testid="facet-help">{help}</p>}
      {searchable && (
        <div className="keyword-search">
          <Search size={13} aria-hidden />
          <input className="input" type="search" value={find} onChange={(e) => setFind(e.target.value)} placeholder={`Find ${title.toLowerCase()}…`} aria-label={`Find ${title.toLowerCase()}`} />
        </div>
      )}
      {shown.slice(0, limit).map(([value, count]) => (
        <button key={value} className="facet-option" aria-pressed={selected.includes(value)} onClick={() => onToggle(value)}>
          <span className="check">{selected.includes(value) && <Check size={11} strokeWidth={3} />}</span>
          {swatch?.(value) && <span className="swatch" style={{ background: swatch(value) }} />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label(value)}</span>
          <span className="count">{approximate && count > 0 ? approxCount(count) : count}</span>
        </button>
      ))}
      {searchable && needle && !shown.length && <p className="faint" style={{ fontSize: 12, margin: '2px 6px' }}>Nothing matches “{find.trim()}”.</p>}
    </div>
  );
}

/**
 * The Library's actions on its whole list, shown while fewer than two assets are selected (1.2.0: with two or more,
 * the selection bar acts on the selection instead). 1.0.0-rc.13 second round (Mikael): at most BULK_ACTION_LIMIT
 * assets per action, for every user; above it the action is disabled with the reason. A list with more matches than
 * are loaded counts all its matches.
 */
function overLimit(eligible: number, items: AssetSummary[], total: number): string | null {
  const n = Math.max(eligible, total > items.length ? total : 0);
  return n > BULK_ACTION_LIMIT ? `Select at most ${BULK_ACTION_LIMIT} (${n.toLocaleString('en-US')} selected): search or filter the list down first` : null;
}

/** Send to edit (1.0.0-rc.6) from the Library: the clips in the list, as a stringout for the NLE. */
function SendListToEdit({ items, total }: { items: AssetSummary[]; total: number }) {
  const me = useMe().data;
  const [open, setOpen] = useState(false);
  const media = items.filter((a) => a.mediaKind === 'video' || a.mediaKind === 'audio' || a.mediaKind === 'image');
  if (!me?.permissions.includes('export.create') || !me.permissions.includes('asset.download') || !media.length) return null;
  const tooMany = overLimit(media.length, items, total);
  return (
    <>
      <button className="btn ghost sm" onClick={() => setOpen(true)} data-testid="library-send-to-edit" disabled={Boolean(tooMany)}
        title={tooMany ?? `Send the clips in this list to an NLE through an edit location${media.length < items.length ? ` (${items.length - media.length} not media, skipped)` : ''}`}>
        <Clapperboard size={13} /> Send {media.length} to edit…
      </button>
      {open && !tooMany && <SendToEditDialog subject={{ kind: 'clips', assetIds: media.map((a) => a.id), name: 'Library selects' }} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Run QC on the assets in this list (1.0.0-rc.8): those already checked or running are skipped. */
function BulkRunQc({ items, total }: { items: AssetSummary[]; total: number }) {
  const me = useMe().data;
  const [open, setOpen] = useState(false);
  const media = items.filter((a) => a.status === 'ready' || a.status === 'partial').filter((a) => a.mediaKind && a.mediaKind !== 'graphic');
  if (!me?.permissions.includes('asset.qc') || !media.length) return null;
  const tooMany = overLimit(media.length, items, total);
  return (
    <>
      <button className="btn ghost sm" onClick={() => setOpen(true)} data-testid="bulk-run-qc" disabled={Boolean(tooMany)}
        title={tooMany ?? `Run QC on ${media.length} asset${media.length === 1 ? '' : 's'} in this list${media.length < items.length ? ` (${items.length - media.length} without media to check are skipped)` : ''}; assets already checked or being checked are skipped`}>
        <BadgeCheck size={13} /> Run QC on {media.length}
      </button>
      {open && !tooMany && <RunQcDialog assetIds={media.map((a) => a.id)} name="the list" skippedBefore={items.length - media.length} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Restore the assets in this list whose original is archived (filter Original: Archived first). */
function BulkRestore({ items, total }: { items: AssetSummary[]; total: number }) {
  const canRestore = useCanRestore();
  const bulk = useBulkRestore();
  const archived = items.filter((a) => a.availability === 'archived');
  if (!canRestore || archived.length < 2) return null;
  const tooMany = overLimit(archived.length, items, total);
  return (
    <button className="btn ghost sm" disabled={bulk.isPending || Boolean(tooMany)} data-testid="bulk-restore"
      title={tooMany ?? `Restore the originals of the ${archived.length} archived assets in this list${archived.length < items.length ? ` (${items.length - archived.length} not archived, skipped)` : ''}`}
      onClick={() => {
        if (!window.confirm(`Restore the originals of ${archived.length} archived asset${archived.length === 1 ? '' : 's'}${archived.length < items.length ? ` (${items.length - archived.length} not archived are skipped)` : ''}? The archive may charge for retrievals.`)) return;
        bulk.mutate(archived.map((a) => a.id), {
          onSuccess: (r) => toast(`Restores requested: ${r.queued}${r.alreadyRestoring ? `, ${r.alreadyRestoring} already restoring` : ''}${r.skipped.length ? `, ${r.skipped.length} skipped` : ''}`),
          onError: (e) => toast(errorMessage(e), 'error'),
        });
      }}>
      <Archive size={13} /> Restore {archived.length} archived
    </button>
  );
}

export function ResultsPane({ f, query, schema, strata, categories, locations, cols, focusedId, selection, onSelect, onFocusItem, onCheck, onOpen, onMoment, onSaveSearch, onListKey, gridRef, loadMore, canUpload, groupedView = false, groupedItems, onVisibleItems }: {
  f: LibraryFilterState; query: ReturnType<typeof useLibrary>; schema: MetadataSchemaDefinition | undefined; strata: StratumDTO[];
  categories: AssetCategoryDTO[]; locations: StorageLocationSummary[]; cols: ReturnType<typeof useLibraryColumns>;
  /** The Inspector's asset (the roving tab stop); `selection` is the multi-select (1.2.0). */
  focusedId: string | null; selection: LibrarySelection;
  onSelect: (id: string, gesture: SelectGesture) => void; onFocusItem: (id: string) => void; onCheck: (id: string, gesture: SelectGesture) => void;
  onOpen: (id: string) => void; onMoment: (id: string, seconds: number, subtitle?: boolean) => void;
  onSaveSearch: () => void; onListKey: (e: ReactKeyboardEvent) => void; gridRef: React.RefObject<HTMLDivElement | null>; loadMore: (node: HTMLDivElement | null) => void; canUpload: boolean;
  /**
   * 1.4.0 (G3): the Grouped view is on (the table, Grouped; the flat list is not fetched then), its asset rows in their
   * visible order (`groupedItems`), and where it reports them (the Library's selection and keys use them).
   */
  groupedView?: boolean; groupedItems?: AssetSummary[];
  onVisibleItems?: (items: AssetSummary[]) => void;
}) {
  const { filters, setFilters, toggle, advanced, setAdvanced, view, setView, sort, setSort, q, hasFilters, clearAll, group } = f;
  const grouped = groupedView && Boolean(onVisibleItems);
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const entry = useEntryOrder(items);
  // With two or more selected the selection bar acts on them; otherwise the whole-list buttons work as before. In the
  // Grouped view they are the tree's asset rows (its own bar, kept in view over the rows, shows them).
  const chosen = selection.selectedOf(grouped ? groupedItems ?? [] : items);
  const multi = chosen.length >= 2;
  const isSelected = (id: string) => (selection.count ? selection.has(id) : id === focusedId);
  const chips = [
    ...filters.status.map((v) => ({ key: 'status' as const, v, label: STATUS_LABELS[v as keyof typeof STATUS_LABELS] ?? v })),
    ...filters.kind.map((v) => ({ key: 'kind' as const, v, label: mediaKindLabel(v) })),
    ...filters.location.map((v) => ({ key: 'location' as const, v, label: locations.find((l) => l.id === v)?.name ?? 'Storage' })),
    ...filters.category.map((v) => ({ key: 'category' as const, v, label: v === 'none' ? 'No class' : categories.find((c) => c.id === v)?.name ?? 'Removed class' })),
    ...Object.entries(filters.fields).flatMap(([id, values]) =>
      values.map((v) => ({ key: `field:${id}` as FacetKey, v, label: facetLabel(schema, id, v) })),
    ),
  ];
  return (
    <section className="pane" aria-label="Assets">
      {/* Wraps onto a second row in a narrow pane rather than cutting controls off (fixes after rc.9 testing). */}
      <div className="toolbar library-toolbar" data-testid="library-toolbar">
        <span className="count" data-testid="result-count">
          {/* Above 10,000 matches the total is an estimate (1.0.0-rc.13): "Showing 100 of about 1.2 million". */}
          {grouped ? `${(groupedItems ?? []).length.toLocaleString('en-US')} asset${groupedItems?.length === 1 ? '' : 's'} shown` : query.isPending ? 'Loading…' : query.data?.totalExact === false
            ? `Showing ${items.length.toLocaleString('en-US')} of ${aboutCount(total)}`
            : items.length < total ? `Showing ${items.length} of ${total}` : `${total} asset${total === 1 ? '' : 's'}`}
        </span>
        {filters.added && (
          <span className="chip" key="added">Added {ADDED_LABELS[filters.added].toLowerCase()}<button aria-label="Remove filter Added" onClick={() => setFilters((x) => ({ ...x, added: null }))}><X size={12} /></button></span>
        )}
        {filters.metadata && (
          <span className="chip" key="metadata">Metadata {METADATA_FILTER_LABELS[filters.metadata].toLowerCase()}<button aria-label="Remove filter Metadata" onClick={() => setFilters((x) => ({ ...x, metadata: null }))}><X size={12} /></button></span>
        )}
        {filters.availability && (
          <span className="chip" key="availability">Original {AVAILABILITY_FILTER_LABELS[filters.availability].toLowerCase()}<button aria-label="Remove filter Original" onClick={() => setFilters((x) => ({ ...x, availability: null }))}><X size={12} /></button></span>
        )}
        {filters.qc && (
          <span className="chip" key="qc">QC {QC_FILTER_LABELS[filters.qc].toLowerCase()}<button aria-label="Remove filter QC" onClick={() => setFilters((x) => ({ ...x, qc: null }))}><X size={12} /></button></span>
        )}
        <MasterFilterChips f={f} />
        <RightsFilterChip f={f} />

        {usableAdvanced(advanced) && advancedSummary(advanced, searchTargets(schema, strata)).map((text) => (
          <span className="chip" key={text}>{text}<button aria-label="Clear advanced search" onClick={() => setAdvanced(EMPTY_ADVANCED)}><X size={12} /></button></span>
        ))}
        {chips.map((c) => (
          <span className="chip" key={`${c.key}:${c.v}`}>
            {c.label}
            <button aria-label={`Remove filter ${c.label}`} onClick={() => toggle(c.key, c.v)}><X size={12} /></button>
          </span>
        ))}
        <div style={{ flex: 1 }} />
        <button className="btn ghost sm" onClick={onSaveSearch} disabled={!hasFilters} data-testid="save-search"
          title={hasFilters ? 'Keep this search, with its filters, sort and view' : 'Search or filter first, then save it'}>
          <Bookmark size={13} /> Save search…
        </button>
        {/* The whole-list buttons act on the flat list, which the Grouped view does not load. */}
        {!multi && !grouped && <BulkRestore items={items} total={total} />}
        {!multi && !grouped && <BulkRunQc items={items} total={total} />}
        {!multi && !grouped && <SendListToEdit items={items} total={total} />}
        <select className="speed-select" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value as LibraryFilters['sort'])}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="title">Title A–Z</option>
          <option value="duration">Longest</option>
          <option value="size">Largest</option>
          {/* Series order only while a group filter is set (librarySort; the API refuses it without one). */}
          {GROUP_SORT_OPTION && group && <option value={GROUP_SORT_OPTION.value}>{GROUP_SORT_OPTION.label}</option>}
        </select>
        {view === 'table' && <ColumnsMenu all={cols.all} ids={cols.ids} set={cols.set} customised={cols.customised} />}
        <Segmented role="group" label="View" value={view} onChange={setView} options={[
          { value: 'grid', label: <LayoutGrid size={14} />, title: 'Grid (G)' },
          { value: 'table', label: <List size={14} />, title: 'Table (G)' },
        ]} />
        {/* 1.4.0 (G3): Grouped, for the table. */}
        {view === 'table' && <GroupedToggle f={f} />}
      </div>
      {multi && !grouped && <LibrarySelectionBar selected={chosen} onClear={selection.clear} />}
      <div className="pane-body" onKeyDown={onListKey}>
        {grouped ? (
          <GroupedResults f={f} cols={cols} focusedId={focusedId} selection={selection} onSelect={onSelect} onFocusItem={onFocusItem} onCheck={onCheck}
            onOpen={onOpen} onVisibleItems={onVisibleItems!} />
        ) : query.isError ? (
          <div className="empty"><div className="empty-inner">
            <ErrorBanner alert title="The library could not be loaded"><span>{errorMessage(query.error)}</span>
              <div className="actions"><button className="btn sm" onClick={() => void query.refetch()}><RotateCcw size={13} /> Try again</button></div></ErrorBanner>
          </div></div>
        ) : query.isPending ? (
          <div className="grid">{Array.from({ length: 8 }, (_, i) => (
            <div className="card" key={i}><div className="thumb skeleton" /><div className="card-body"><div className="skeleton" style={{ height: 14, width: '70%' }} /><div className="skeleton" style={{ height: 12, width: '40%' }} /></div></div>
          ))}</div>
        ) : items.length === 0 ? (
          hasFilters ? (
            <EmptyState icon={<SearchX size={24} />} title="No assets match">
              <p>Nothing matches {q ? <>“{q}”</> : 'these filters'}. Try fewer filters or a broader search.</p>
              <button className="btn" onClick={clearAll} data-testid="clear-all">Clear all</button>
            </EmptyState>
          ) : (
            <EmptyState testId="library-empty" icon={<FolderOpen size={24} />} title="Your library is empty">
              <p>Upload footage or audio. PRISM keeps your original safe in managed storage, inspects it, and prepares a browser proxy, poster, scrub thumbnails and waveform.</p>
              {canUpload ? <button className="btn primary" onClick={pickFiles}><Plus size={15} /> Upload media</button> : <p className="faint">Your role can browse but not upload.</p>}
              {canUpload && <p className="faint" style={{ fontSize: 12 }}>Or drag files anywhere onto this window · <kbd>U</kbd></p>}
            </EmptyState>
          )
        ) : view === 'grid' ? (
          <div className={`grid${selection.count ? ' selecting' : ''}`} ref={gridRef} role="listbox" aria-label="Assets" aria-multiselectable="true" data-testid="asset-grid">
            {items.map((a) => (
              <AssetCard key={a.id} asset={a} enter={entry.get(a.id)} selected={isSelected(a.id)} checked={selection.has(a.id)} focused={a.id === focusedId}
                onSelect={(g) => onSelect(a.id, g)} onFocus={() => onFocusItem(a.id)} onCheck={(g) => onCheck(a.id, g)} onOpen={() => onOpen(a.id)}
                moments={query.data?.moments?.[a.id]} onMoment={(seconds, subtitle) => onMoment(a.id, seconds, subtitle)} />
            ))}
          </div>
        ) : (
          <AssetTable items={items} entry={entry} columns={cols.shown} focusedId={focusedId} isSelected={isSelected} isChecked={selection.has}
            onSelect={onSelect} onFocus={onFocusItem} onCheck={onCheck} onCheckAll={(all) => (all ? selection.selectAll(items.map((a) => a.id)) : selection.clear())} onOpen={onOpen} />
        )}
        {/* The flat list's Load more; the Grouped view pages each level itself. */}
        {!grouped && items.length > 0 && !query.isError && (
          <div ref={loadMore} className="load-more" data-testid="load-more" aria-live="polite">
            {query.isFetchingNextPage ? 'Loading more…' : query.hasNextPage ? (
              <button className="btn ghost sm" onClick={() => void query.fetchNextPage()}>Load more</button>
            ) : libraryCapped(items.length, total, query.data?.totalExact) ? (
              // 1.8.1: the Library pages to MAX_LIBRARY_OFFSET rows (the API's OFFSET_TOO_DEEP message).
              <span className="faint" data-testid="library-capped">{RELEASE_181_ERRORS.OFFSET_TOO_DEEP.message}</span>
            ) : items.length < total && query.data?.totalExact !== false ? null : items.length > LIBRARY_PAGE_SIZE ? <span className="faint">All {items.length.toLocaleString('en-US')} assets loaded</span> : null}
          </div>
        )}
      </div>
    </section>
  );
}
